import { useDraggable, useDroppable } from '@dnd-kit/core'
import { AlertTriangle, Check, GripVertical, Loader2 } from 'lucide-react'
import { format } from 'date-fns/format'
import { isSameMonth } from 'date-fns/isSameMonth'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CHANNEL_META, PlatformIcon, type Channel } from 'components'
import type { CalendarEntry } from '../types'
import {
  CalendarPopoverCard,
  computeIsGenerating,
  computeIsGenerationFailed,
} from './CalendarPopoverCard'
import { CreatePostButton } from './CreatePostButton'
import { useEditsRenderState } from '../../creative/components/EditsApplyingBadge'
import { BorderTravelOverlay } from '../../creative/previews/ShimmerOverlay'

function getChannelLabel(channel?: string): string {
  return CHANNEL_META[(channel ?? 'instagram') as Channel]?.label ?? 'Post'
}

/**
 * Publish status for a month-view bar — collapsed to the three states worth
 * color-coding at a glance in the dense month grid:
 *   live      → acid stripe + ✓ badge
 *   failed    → destructive stripe
 *   scheduled → neutral stripe (the normal queued state)
 * Drafts return null — their dashed border already carries the meaning. Live
 * uses the acid accent to stay consistent with every other "Live" affordance
 * (the week card's live chip, the idea-card status chip), which all use acid.
 */
type MonthBarStatus = 'live' | 'scheduled' | 'failed' | null

function getMonthBarStatus(entry: CalendarEntry): MonthBarStatus {
  if (entry.publishFailure) return 'failed'
  // Generation failed and produced nothing — the honest terminal state (the
  // snake border used to spin forever here). Same destructive coding as a
  // publish failure; the week card / PostModal carry the full story + Retry.
  if (computeIsGenerationFailed(entry)) return 'failed'
  if (entry.type === 'published' || entry.publishedAt) return 'live'
  if (entry.type === 'scheduled_creative' || entry.isApproved || entry.scheduledAt)
    return 'scheduled'
  return null
}

function DraggableCard({ entry, children }: { entry: CalendarEntry; children: React.ReactNode }) {
  const isDraggable = entry.type !== 'published'
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: entry.id,
    data: { entry },
    disabled: !isDraggable,
  })

  return (
    <div
      ref={setNodeRef}
      {...(isDraggable ? listeners : {})}
      className={`group/drag relative ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''} ${
        isDragging ? 'rounded-xl opacity-30 ring-2 ring-primary' : ''
      }`}
    >
      {isDraggable && (
        <div
          {...attributes}
          className="absolute top-1/2 -left-1 z-10 -translate-y-1/2 rounded p-0.5 text-paper-faint opacity-0 transition-opacity group-hover/drag:opacity-100 hover:text-paper-dim"
        >
          <GripVertical size={14} />
        </div>
      )}
      {children}
    </div>
  )
}

function DropPlaceholderCard() {
  return (
    <div
      className="w-full rounded-xl border-2 border-dashed border-primary/30 bg-primary/5"
      style={{ minHeight: 180 }}
    />
  )
}

function MonthEntryBar({
  entry,
  onOpenPost,
}: {
  entry: CalendarEntry
  onOpenPost?: (entry: CalendarEntry) => void
}) {
  const isDraggable = entry.type !== 'published'

  const isGenerating = computeIsGenerating(entry)

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: entry.id,
    data: { entry },
    disabled: !isDraggable,
  })

  const channelLabel = getChannelLabel(entry.channel)
  const status = getMonthBarStatus(entry)
  // Any failed sibling — surfaces the corner badge independent of the bar's
  // status stripe, so a partially-failed published entry can carry BOTH the
  // live ✓ (from `status === 'live'`) and the destructive AlertTriangle badge.
  const isGenerationFailed = computeIsGenerationFailed(entry)
  const hasFailure =
    !!entry.publishFailure ||
    !!entry.platformStatuses?.some(p => p.status === 'failed') ||
    isGenerationFailed
  // The thumbnail is the last-rendered master while composition edits are
  // still being applied server-side. The 36px thumb only fits a corner
  // spinner — it reuses the live ✓ slot (mutually exclusive: never live here).
  const editsRenderState = useEditsRenderState(entry.creativeId)
  const isEditsApplying = editsRenderState !== null && status !== 'live' && !isGenerating

  return (
    <div
      ref={setNodeRef}
      {...(isDraggable ? listeners : {})}
      {...(isDraggable ? attributes : {})}
      onClick={e => {
        e.stopPropagation()
        // Generating bars have nothing to open yet; everything else opens PostModal.
        if (!isGenerating && entry.creativeId) onOpenPost?.(entry)
      }}
      className={`group/slot relative flex items-center gap-1.5 rounded-lg border bg-bg-2 p-1 text-[10px] leading-tight font-medium ${
        isDragging ? 'opacity-30' : ''
      } ${isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${
        isGenerating
          ? 'border-transparent'
          : entry.thumbnailUrl
            ? 'border-line hover:border-line-bright'
            : 'border-dashed border-line-bright hover:border-primary/40'
      }`}
    >
      {/* Snake border for generating entries */}
      {isGenerating && <BorderTravelOverlay speed={4.6} />}

      {/* Publish-status stripe — glance-level state coding for the dense month
          grid. Hidden while generating (the snake border owns that signal). */}
      {status && !isGenerating && (
        <span
          aria-hidden
          className={`absolute top-1 bottom-1 left-0 w-[3px] rounded-full ${
            status === 'live' ? 'bg-acid' : status === 'failed' ? 'bg-destructive' : 'bg-paper-dim'
          }`}
        />
      )}

      {/* Channel icon. The outer wrapper is not clipped so the live ✓ badge can
          sit on the corner. */}
      <div className="relative h-9 w-9 flex-shrink-0">
        <div className="relative h-full w-full overflow-hidden rounded-md bg-bg-3">
          {entry.thumbnailUrl ? (
            <img src={entry.thumbnailUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <PlatformIcon
              channel={entry.channel ?? 'instagram'}
              size={20}
              className="absolute inset-0 m-auto opacity-70"
            />
          )}
        </div>
        {/* Edits applying — the master render is catching up to the user's
            composition edits. Occupies the live ✓ slot (never live here). */}
        {isEditsApplying && entry.thumbnailUrl && (
          <span
            title={editsRenderState === 'failed' ? "Edits didn't apply" : 'Applying your edits'}
            aria-label={
              editsRenderState === 'failed' ? "Edits didn't apply" : 'Applying your edits'
            }
            className="absolute -right-1 -bottom-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-bg-3 ring-2 ring-[color:var(--bg-2)]"
          >
            {editsRenderState === 'failed' ? (
              <AlertTriangle size={9} strokeWidth={3} className="text-destructive" />
            ) : (
              <Loader2
                size={9}
                strokeWidth={3}
                className="animate-spin text-acid motion-reduce:animate-none"
              />
            )}
          </span>
        )}
        {/* Live ✓ — matches the week card's LivePill / idea-card Live button,
            which all use the acid accent for the "Live" check. */}
        {status === 'live' && (
          <span className="absolute -right-1 -bottom-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-acid ring-2 ring-[color:var(--bg-2)]">
            <Check size={9} strokeWidth={3} className="text-ink-on-acid" />
          </span>
        )}
        {/* Destructive failure badge — mirrors the Live ✓ slot but on the top
            corner so it can coexist with the live checkmark on partially-failed
            published entries (user-approved UX). Icon-only; the popover with
            humanized copy lives on the week card and PostModal. */}
        {hasFailure && (
          <span
            className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive ring-2 ring-[color:var(--bg-2)]"
            aria-label={isGenerationFailed ? "Didn't come out" : 'Publish failed'}
            title={isGenerationFailed ? "Didn't come out — open to retry" : 'Publish failed'}
          >
            <AlertTriangle size={9} strokeWidth={3} className="text-white" />
          </span>
        )}
      </div>

      {/* Label */}
      <div className="min-w-0 flex-1">
        <span className="line-clamp-2 leading-tight text-paper">
          {entry.creativeName ?? channelLabel}
        </span>
      </div>
    </div>
  )
}

interface CalendarDayCellProps {
  date: Date
  entries: CalendarEntry[]
  currentMonth: Date
  compact?: boolean
  selectedEntryId?: string
  /** Opens PostModal for an entry (week cards + month bars + popover cards). */
  onOpenPost?: (entry: CalendarEntry) => void
  /** creativeId currently loading its PostModal payload — drives a per-card veil. */
  openingId?: string | null
  popoverOpen?: boolean
  onPopoverToggle?: (dateStr: string) => void
  onPopoverClose?: (dateStr: string) => void
  /** The entry currently being dragged, if any */
  draggingEntry?: CalendarEntry | null
  /** When provided, day tap calls this instead of opening inline popover */
  onDayTap?: (dateStr: string) => void
  /** Teaching state for a zero-entry calendar: render this empty cell's
   *  "+ Add post" hint at rest instead of the default hover-reveal. */
  hintAtRest?: boolean
}

export function CalendarDayCell({
  date,
  entries,
  currentMonth,
  compact,
  onOpenPost,
  openingId,
  popoverOpen = false,
  onPopoverToggle,
  onPopoverClose,
  draggingEntry,
  onDayTap,
  hintAtRest = false,
}: CalendarDayCellProps) {
  const cellRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const inMonth = isSameMonth(date, currentMonth)
  const dateStr = format(date, 'yyyy-MM-dd')

  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const isPast = dateStr < todayStr

  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dateStr}`,
    data: { date: dateStr },
    disabled: isPast,
  })

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node)
      ;(cellRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    },
    [setNodeRef]
  )

  const entryCount = entries.length

  // Click on cell toggles the popover (month view only).
  // Open for any future/today cell whether or not there are entries — empty
  // cells show "Nothing scheduled" + the CreatePostButton in the popover.
  const handleCellClick = useCallback(() => {
    if (!compact) return
    if (onDayTap) {
      onDayTap(dateStr)
      return
    }
    if (!isPast || entryCount > 0) {
      onPopoverToggle?.(dateStr)
    }
  }, [compact, dateStr, isPast, onDayTap, onPopoverToggle, entryCount])

  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (cellRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      // A modal dialog (e.g. the delete-confirm) renders in a portal outside
      // the popover — don't treat clicks while one is open as "outside",
      // which would unmount the popover (and the dialog) mid-action.
      if (document.querySelector('[data-slot="dialog-content"][data-state="open"]')) return
      onPopoverClose?.(dateStr)
    }
    // Use timeout so this listener doesn't catch the click that opened the popover
    const id = setTimeout(() => {
      document.addEventListener('mousedown', handleClick)
    }, 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [popoverOpen, dateStr, onPopoverClose])

  // Compute popover position and max height
  // Strategy: align popover top with cell top when there's room below,
  // otherwise anchor popover bottom to cell bottom so it stays near the entry.
  const getPopoverPos = useCallback(() => {
    if (!cellRef.current) return { top: 0, left: 0, maxHeight: 500, anchorBottom: false }
    const rect = cellRef.current.getBoundingClientRect()
    const popoverWidth = 320
    const popoverEstimate = 400 // estimated popover content height
    const margin = 16
    let left = rect.right + 4

    if (left + popoverWidth > window.innerWidth - margin) {
      left = rect.left - popoverWidth - 4
    }

    const spaceBelow = window.innerHeight - rect.top - margin

    if (spaceBelow >= popoverEstimate) {
      // Plenty of room below — align top of popover with top of cell
      return { top: rect.top, left, maxHeight: Math.min(spaceBelow, 600), anchorBottom: false }
    }

    // Not enough room below — anchor popover bottom to cell bottom
    const maxHeight = Math.min(rect.bottom - margin, 600)
    return { top: rect.bottom, left, maxHeight, anchorBottom: true }
  }, [])

  // Re-measure the popover position when the page scrolls or the window
  // resizes. Without this, `getPopoverPos` only runs at render-time and the
  // `fixed`-positioned portaled node stays anchored to stale viewport
  // coordinates — visually detaching from the cell as the user scrolls.
  // `CreateMomentPopover` in CreatePostButton already does this; keep
  // behavior consistent across portaled menus.
  const [popoverTick, setPopoverTick] = useState(0)
  useEffect(() => {
    if (!popoverOpen) return
    const onChange = () => setPopoverTick(t => t + 1)
    // Capture phase so nested scrollers (the calendar grid itself) also trigger.
    window.addEventListener('scroll', onChange, true)
    window.addEventListener('resize', onChange)
    return () => {
      window.removeEventListener('scroll', onChange, true)
      window.removeEventListener('resize', onChange)
    }
  }, [popoverOpen])

  const handlePopoverClose = useCallback(() => {
    onPopoverClose?.(dateStr)
  }, [dateStr, onPopoverClose])

  // ─── Week view: rich cards (popover-style inline) ────────────────────────

  if (!compact) {
    return (
      <div
        ref={setRefs}
        className={`group/cell relative flex h-full flex-col border-r border-b ${
          inMonth ? 'bg-bg-2' : 'bg-bg-3/50'
        } transition-colors`}
      >
        {/* Scrollable entry cards — date number is in the sticky header row */}
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
          {/* Empty future cell: "+ Add post" — at rest for the teaching cells
              of a zero-entry calendar, hover-reveal otherwise (matches month) */}
          {!isPast && entries.length === 0 && (
            <div onClick={e => e.stopPropagation()}>
              <CreatePostButton date={dateStr} variant="hint" alwaysVisible={hintAtRest} />
            </div>
          )}

          {/* The card is a self-contained clickable tile (own border + generating
              overlay); the whole-card click opens PostModal. DraggableCard adds
              the drag listeners — the 8px PointerSensor threshold separates a
              click from a drag, so both coexist on the same element. */}
          {entries.map(entry => (
            <DraggableCard key={entry.id} entry={entry}>
              <CalendarPopoverCard
                entry={entry}
                onClose={() => {}}
                onOpenPost={onOpenPost}
                opening={openingId === entry.creativeId}
              />
            </DraggableCard>
          ))}

          {/* Ghost placeholder card when dragging over this cell (not the source cell) */}
          {isOver && draggingEntry && draggingEntry.date !== dateStr && <DropPlaceholderCard />}

          {/* Subtle "+ Add post" hint under existing entries (hover effect) */}
          {!isPast && entries.length > 0 && <CreatePostButton date={dateStr} variant="hint" />}
        </div>
      </div>
    )
  }

  // ─── Month view: colored bars + click popover ────────────────────────────

  // `popoverTick` is read here so the scroll/resize effect above actually
  // causes a fresh measurement on every tick. It's a deliberate subscribe —
  // do not remove without also changing the effect.
  void popoverTick
  const popoverPos = popoverOpen
    ? getPopoverPos()
    : { top: 0, left: 0, maxHeight: 500, anchorBottom: false }

  return (
    <div
      ref={setRefs}
      onClick={handleCellClick}
      className={`group/cell relative flex min-h-[80px] flex-col border-r border-b p-1.5 ${
        inMonth ? 'bg-bg-2' : 'bg-bg-3/50'
      } ${isOver ? 'bg-primary/5' : ''} ${
        popoverOpen ? 'bg-bg-3 ring-1 ring-primary/20' : ''
      } cursor-pointer transition-colors hover:bg-bg-3/80`}
    >
      {/* Day number */}
      <div className="mb-1">
        <span className="flex h-6 w-6 items-center justify-center text-xs font-medium text-paper-dim">
          {format(date, 'd')}
        </span>
      </div>

      {/* Empty future cell: "+ Add post" right under the day number — at rest
          for the teaching cells of a zero-entry calendar, hover-reveal otherwise */}
      {!isPast && entries.length === 0 && (
        <div onClick={e => e.stopPropagation()}>
          <CreatePostButton date={dateStr} variant="hint" alwaysVisible={hintAtRest} />
        </div>
      )}

      {/* Entry bars with thumbnails and status */}
      {entries.length > 0 && (
        <div className="flex flex-col gap-0.5">
          {entries.slice(0, 3).map(entry => (
            <MonthEntryBar key={entry.id} entry={entry} onOpenPost={onOpenPost} />
          ))}
          {entries.length > 3 && (
            <span className="px-1 text-[10px] font-medium text-paper-faint">
              +{entries.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Subtle Add post hint for cells that already have entries */}
      {!isPast && entries.length > 0 && (
        <div className="mt-auto pt-1" onClick={e => e.stopPropagation()}>
          <CreatePostButton date={dateStr} variant="hint" alwaysVisible />
        </div>
      )}

      {/* Ghost placeholder bar in month view */}
      {isOver && draggingEntry && draggingEntry.date !== dateStr && (
        <div className="mt-0.5 rounded border border-dashed border-primary/30 bg-primary/5 py-1.5" />
      )}

      {/* Click popover via portal */}
      {popoverOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-50 flex w-[320px] flex-col overflow-hidden rounded-lg border bg-bg-2 shadow-lg"
            style={
              popoverPos.anchorBottom
                ? {
                    bottom: window.innerHeight - popoverPos.top,
                    left: popoverPos.left,
                    maxHeight: popoverPos.maxHeight,
                  }
                : { top: popoverPos.top, left: popoverPos.left, maxHeight: popoverPos.maxHeight }
            }
          >
            {/* Popover header */}
            <div className="flex-shrink-0 border-b px-3 py-2">
              <p className="text-sm font-medium text-paper">{format(date, 'EEEE, MMM d')}</p>
              <p className="text-xs text-paper-faint">
                {entries.length === 0
                  ? 'Nothing scheduled'
                  : `${entries.length} ${entries.length === 1 ? 'post' : 'posts'} scheduled`}
              </p>
            </div>

            {/* Rich entry preview cards */}
            {entries.length > 0 && (
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
                {entries.map(entry => (
                  <CalendarPopoverCard
                    key={entry.id}
                    entry={entry}
                    onClose={handlePopoverClose}
                    onOpenPost={onOpenPost}
                    opening={openingId === entry.creativeId}
                  />
                ))}
              </div>
            )}

            {/* Create post action */}
            {!isPast && (
              <div className="flex-shrink-0 border-t p-2">
                <CreatePostButton date={dateStr} variant="full" onCreated={handlePopoverClose} />
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
